import { useState, useEffect } from 'react'
import { Box, IconButton, Select, MenuItem, Typography, CircularProgress, Tooltip } from '@mui/material'
import { AddPhotoAlternate } from '@mui/icons-material'
import { Delete } from '@mui/icons-material'
import React from 'react'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { usePhotosForInventoryObject } from '@/hooks/inventory/usePhotosForInventoryObject'
import { PhotoPosition, VehiclePhoto } from '@otw/models'

interface VehiclePhotosRendererProps {
  data: any
  node: any
  canModify?: boolean
  onUploadStateChange?: (vehicleId: string, isUploading: boolean) => void
}

export const VehiclePhotosRenderer: React.FC<VehiclePhotosRendererProps> = props => {
  const [uploadingFiles, setUploadingFiles] = useState<
    {
      file: File
      previewBlobUrl: string
      loading: boolean
    }[]
  >([])
  const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()
  const vehicle = props.data
  const canModify = props.canModify !== undefined ? props.canModify : true

  useEffect(() => {
    props.onUploadStateChange?.(vehicle.id, uploadingFiles.length > 0)
  }, [uploadingFiles.length, vehicle.id, props.onUploadStateChange])

  const {
    data: photos,
    deletePhotos,
    uploadPhotos,
    updatePosition,
    isLoading,
  } = usePhotosForInventoryObject('master_inventory', vehicle.id, {
    enabled: props.node.expanded && vehicle.photo_storage_path !== '',
  })

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return

    const filesArray = Array.from(e.target.files)
    const filesWithPreview = filesArray.map(file => ({
      file,
      previewBlobUrl: URL.createObjectURL(file),
      loading: true,
    }))

    setUploadingFiles(filesWithPreview)
    uploadPhotos(filesWithPreview, {
      onSuccess: () => {
        showSuccess('Photos uploaded successfully')
        setUploadingFiles([])
      },
      onError: (error: Error) => {
        showError('Error uploading photos')
        console.error('Error uploading photos:', error)
        setUploadingFiles([])
      },
    })
  }

  return (
    <Box sx={{ p: 2, bgcolor: 'primary.light' }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* Add Photo Card - Only show if user has modify permission */}
        {canModify ? (
          <Box
            component='label'
            sx={{
              width: 200,
              height: 200,
              border: '2px dashed',
              borderColor: 'text.secondary',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'text.disabled',
                bgcolor: 'action.hover',
              },
            }}
          >
            <input type='file' hidden multiple accept='image/*' onChange={handleFileUpload} />
            <AddPhotoAlternate sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant='body1' color='text.secondary'>
              Add Photos
            </Typography>
          </Box>
        ) : (
          <Tooltip title="You don't have permission to add photos">
            <Box
              sx={{
                width: 200,
                height: 200,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.5,
              }}
            >
              <AddPhotoAlternate sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant='body1' color='text.disabled'>
                Cannot Add Photos
              </Typography>
            </Box>
          </Tooltip>
        )}

        {/* Loading Photo Cards */}
        {uploadingFiles.map((file, index) => (
          <Box
            key={`loading-${index}`}
            sx={{
              width: 200,
              height: 200,
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0, 0, 0, 0.1)',
            }}
          >
            <CircularProgress />
            <img
              src={file.previewBlobUrl}
              alt='Uploading...'
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.5,
                position: 'absolute',
              }}
            />
          </Box>
        ))}

        {/* Photo Cards */}
        {Array.isArray(photos) &&
          photos.map((photo: VehiclePhoto) => (
            <Box
              key={photo.url}
              sx={{
                width: 200,
                height: 200,
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 3,
                '&:hover .photo-overlay': {
                  opacity: canModify ? 1 : 0, // Only show overlay if user can modify
                },
              }}
            >
              <img
                src={photo.url}
                alt={photo.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              {/* Overlay with actions - Only allow actions if user has permission */}
              {canModify ? (
                <Box
                  className='photo-overlay'
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 1,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Select
                      value={photo.position || ''}
                      onChange={e =>
                        updatePosition({
                          name: photo.name,
                          position: e.target.value as PhotoPosition,
                        })
                      }
                      sx={{
                        color: 'white',
                        '.MuiSelect-icon': { color: 'white' },
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                      }}
                      size='small'
                    >
                      {['main', 'front', 'back', 'left', 'right', 'interior'].map(pos => (
                        <MenuItem key={pos} value={pos}>
                          {pos}
                        </MenuItem>
                      ))}
                    </Select>
                    <IconButton onClick={() => deletePhotos([photo])} sx={{ color: 'error.main' }}>
                      <Delete />
                    </IconButton>
                  </Box>
                  <Typography variant='caption' sx={{ color: 'white', textAlign: 'center' }}>
                    {photo.position || 'No position set'}
                  </Typography>
                </Box>
              ) : (
                <Tooltip title="You don't have permission to manage photos" enterDelay={500} leaveDelay={200}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          ))}
      </Box>
      {!canModify && photos && photos.length === 0 && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant='body1' color='text.secondary'>
            No photos available for this vehicle.
          </Typography>
        </Box>
      )}
    </Box>
  )
}
